import type { ComputedRef, Ref } from 'vue'

import type { WorkflowInstance } from '@/types/workflow'

import { clampTimelineStartMs, normalizeNarrationTimelineSegments } from './finalAudioTimeline'
import { normalizeAudioMixControls } from './finalAudioMixDraft'
import { cloneScene } from './storyboardSceneDrafts'
import type {
  AudioMixControlDraft,
  FinalAudioNarrationSegmentDraft,
  FinalVideoPlaybackSource,
  StoryboardWorkspaceState,
  UploadedWorkspaceAudio,
  UploadedWorkspaceVideo,
} from './types'

interface PersistOptions {
  successMessage?: string
  silent?: boolean
}

interface UseStoryboardWorkspacePersistenceOptions {
  projectId: number
  storyboardWorkspaceInputKey: string
  storyboardWorkspaceState: ComputedRef<StoryboardWorkspaceState>
  workflow: Ref<WorkflowInstance | null>
  sceneVideoFeedbackDrafts: Ref<Record<number, string>>
  finalVoiceoverDraft: Ref<string>
  finalVoiceoverMode: Ref<string>
  finalVoiceoverTone: Ref<string>
  finalBgmPlan: Ref<string>
  finalTtsScript: Ref<string>
  finalTtsStartMs: Ref<number>
  finalTtsVoiceId: Ref<string>
  finalTtsProvider: Ref<string>
  finalTtsSpeed: Ref<number>
  finalTtsTimelineSegments: Ref<FinalAudioNarrationSegmentDraft[]>
  uploadedFinalBgmAudio: Ref<UploadedWorkspaceAudio | null>
  audioMixControls: Ref<AudioMixControlDraft>
  audioMixPreviewVideo: Ref<UploadedWorkspaceVideo | null>
  audioMixDeliveryVideo: Ref<UploadedWorkspaceVideo | null>
  preferredFinalVideoSource: Ref<FinalVideoPlaybackSource>
  updateWorkflowInputData: (projectId: number, payload: { input_data: Record<string, any> }) => Promise<WorkflowInstance>
  success: (message: string) => void
}

export function useStoryboardWorkspacePersistence(options: UseStoryboardWorkspacePersistenceOptions) {
  function getStoryboardWorkspaceStatePayload(): StoryboardWorkspaceState {
    const currentState = cloneScene(options.storyboardWorkspaceState.value || {})
    return {
      ...currentState,
      scene_video_feedbacks: {
        ...(currentState.scene_video_feedbacks || {}),
        ...Object.entries(options.sceneVideoFeedbackDrafts.value).reduce<Record<string, string>>((result, [sceneNumber, feedback]) => {
          result[String(sceneNumber)] = String(feedback || '')
          return result
        }, {}),
      },
      final_delivery: {
        ...(currentState.final_delivery || {}),
        voiceover_note: options.finalVoiceoverDraft.value,
        voiceover_mode: options.finalVoiceoverMode.value,
        voiceover_tone: options.finalVoiceoverTone.value,
        bgm_plan: options.finalBgmPlan.value,
        tts_script_draft: options.finalTtsScript.value,
        tts_start_ms_draft: clampTimelineStartMs(options.finalTtsStartMs.value),
        tts_timeline_segments: options.finalTtsTimelineSegments.value,
        tts_script: options.finalTtsScript.value,
        tts_voice_id: options.finalTtsVoiceId.value,
        tts_provider: options.finalTtsProvider.value,
        tts_speed: options.finalTtsSpeed.value,
        generated_tts_audio: options.finalTtsTimelineSegments.value[options.finalTtsTimelineSegments.value.length - 1]?.audio || null,
        uploaded_bgm_audio: options.uploadedFinalBgmAudio.value,
        audio_mix_controls: options.audioMixControls.value,
        audio_mix_preview_video: options.audioMixPreviewVideo.value,
        audio_mix_delivery_video: options.audioMixDeliveryVideo.value,
        preferred_final_video_source: options.preferredFinalVideoSource.value,
        mixed_final_video: options.audioMixPreviewVideo.value,
        uploaded_final_video: currentState.final_delivery?.uploaded_final_video || null,
      },
    }
  }

  async function persistStoryboardWorkspaceState(nextState: StoryboardWorkspaceState, persistOptions: PersistOptions = {}) {
    options.workflow.value = await options.updateWorkflowInputData(options.projectId, {
      input_data: {
        [options.storyboardWorkspaceInputKey]: nextState,
      },
    })

    if (!persistOptions.silent && persistOptions.successMessage) {
      options.success(persistOptions.successMessage)
    }
  }

  function hydrateStoryboardWorkspaceState() {
    const currentState = options.storyboardWorkspaceState.value || {}
    options.sceneVideoFeedbackDrafts.value = Object.entries(currentState.scene_video_feedbacks || {}).reduce<Record<number, string>>((result, [sceneNumber, feedback]) => {
      const numericSceneNumber = Number(sceneNumber)
      if (numericSceneNumber) {
        result[numericSceneNumber] = String(feedback || '')
      }
      return result
    }, {})

    const finalDelivery = currentState.final_delivery || {}
    options.finalVoiceoverDraft.value = String(finalDelivery.voiceover_note || '')
    options.finalVoiceoverMode.value = String(finalDelivery.voiceover_mode || options.finalVoiceoverMode.value || 'keep_current_audio')
    options.finalVoiceoverTone.value = String(finalDelivery.voiceover_tone || options.finalVoiceoverTone.value || 'calm')
    options.finalBgmPlan.value = String(finalDelivery.bgm_plan || options.finalBgmPlan.value || 'keep_current')
    options.finalTtsScript.value = String(finalDelivery.tts_script_draft || finalDelivery.tts_script || '')
    options.finalTtsStartMs.value = clampTimelineStartMs(finalDelivery.tts_start_ms_draft)
    options.finalTtsVoiceId.value = String(finalDelivery.tts_voice_id || '')
    options.finalTtsProvider.value = String(finalDelivery.tts_provider || '')
    options.finalTtsSpeed.value = Number(finalDelivery.tts_speed ?? 1) || 1
    options.finalTtsTimelineSegments.value = normalizeNarrationTimelineSegments(finalDelivery.tts_timeline_segments, {
      script: finalDelivery.tts_script,
      generatedAudio: finalDelivery.generated_tts_audio || null,
      provider: finalDelivery.tts_provider,
      voiceId: finalDelivery.tts_voice_id,
      speed: Number(finalDelivery.tts_speed ?? 1) || 1,
    })
    options.uploadedFinalBgmAudio.value = finalDelivery.uploaded_bgm_audio || null
    options.audioMixControls.value = normalizeAudioMixControls(finalDelivery.audio_mix_controls)
    options.audioMixPreviewVideo.value = finalDelivery.audio_mix_preview_video || finalDelivery.mixed_final_video || null
    options.audioMixDeliveryVideo.value = finalDelivery.audio_mix_delivery_video || null
    options.preferredFinalVideoSource.value = finalDelivery.preferred_final_video_source === 'audio_mix_delivery'
      || finalDelivery.preferred_final_video_source === 'uploaded_final'
      || finalDelivery.preferred_final_video_source === 'system'
      ? finalDelivery.preferred_final_video_source
      : 'system'
  }

  return {
    getStoryboardWorkspaceStatePayload,
    hydrateStoryboardWorkspaceState,
    persistStoryboardWorkspaceState,
  }
}